

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithEmail, signInWithGoogle, user, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      console.log('[Login] User already logged in, redirecting...');
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setError(decodeURIComponent(errorParam));
    }
  }, [searchParams]);

  useEffect(() => {
    const checkAndClearBadSession = async () => {
      try {
        const { error } = await supabase.auth.getSession();

        if (error && error.message.includes('refresh_token')) {
          console.log('Clearing invalid session...');
          await supabase.auth.signOut();
          localStorage.clear();
          sessionStorage.clear();
        }
      } catch (err) {
        console.error('Session check error:', err);
      }
    };

    checkAndClearBadSession();
  }, [supabase]);
  
  useEffect(() => {
  // Test if we can write cookies at all
  document.cookie = "test=123; path=/; SameSite=Lax";
  const canWriteCookies = document.cookie.includes("test=123");
  console.log('[Cookie Test]', {
    canWriteCookies,
    allCookies: document.cookie,
    domain: window.location.hostname,
    protocol: window.location.protocol
  });
}, []);
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('[Login] Starting sign-in process...');
      const { error: signInError } = await signInWithEmail(formData.email, formData.password);

      if (signInError) throw signInError;

      console.log('[Login] ✅ Sign-in successful!');

      const returnTo = searchParams.get('returnTo');

      await new Promise(resolve => setTimeout(resolve, 500));

      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('[Login] Sign-in error:', err);
      if (err.message?.includes('rate limit')) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setError(err.message || 'An error occurred during sign in');
      }
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    const returnTo = searchParams.get('returnTo');
    const redirectUrl = returnTo
      ? `${window.location.origin}${returnTo}`
      : `${window.location.origin}/`;

    const { error: signInError } = await signInWithGoogle(redirectUrl);

    if (signInError) {
      setError(signInError.message || 'An error occurred during Google sign in');
      setGoogleLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: Form */}
      <div className="flex flex-col justify-between px-6 py-8 md:px-16 md:py-10 bg-white">
        {/* Brand */}
        <div className="flex items-center justify-center">
          <img
            src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/png%20logo.png"
            alt="Interview Trainer"
            className="h-20 w-auto"
          />
        </div>

        {/* Sign in */}
        <div className="max-w-md mx-auto w-full mt-10 mb-12">
          <h1 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-3">
            Welcome back
          </h1>
          <p className="text-slate-500 mb-8">
            Sign in to continue your interview practice journey.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            className="w-full mb-4 rounded-full border border-slate-200 bg-white text-slate-700 py-2.5 text-sm font-medium shadow-sm hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {googleLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700"></div>
            ) : (
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            Continue with Google
          </button>

          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">Or continue with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email address
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50"
                placeholder="you@example.com"
                disabled={loading || googleLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/70 focus:border-emerald-500 bg-slate-50"
                placeholder="Enter your password"
                disabled={loading || googleLoading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full mt-4 rounded-full bg-emerald-600 text-white py-2.5 text-sm font-medium shadow-sm hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center md:text-left">
          Don&apos;t have an account?{' '}
          <a href={`/signup${searchParams.get('returnTo') ? `?returnTo=${encodeURIComponent(searchParams.get('returnTo')!)}` : ''}`} className="text-emerald-600 hover:underline">
            Create one
          </a>
        </p>
      </div>

      {/* Right: Image / gradient */}
      <div className="hidden md:block relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-green-400 to-teal-300 opacity-90"></div>
        <div className="absolute inset-8 rounded-3xl border border-white/40 backdrop-blur-sm bg-white/10 flex flex-col justify-between p-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-4">
              Tailored AI coaching
            </p>
            <h2 className="text-3xl font-semibold text-white mb-3">
              Get interview-ready in days, not months.
            </h2>
            <p className="text-sm text-white/80 max-w-sm mb-6">
              Practice with realistic interview scenarios, get content and behaviour scores,
              and track your progress over time.
            </p>

            {/* Preview Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30">
              <img
                src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/Interview%20page.jpg"
                alt="Interview dashboard preview"
                className="w-full h-auto"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 text-white/90">
              <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
              <span className="text-sm">Personalized mock interviews based on your career goals</span>
            </div>
            <div className="flex items-start gap-3 text-white/90">
              <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
              <span className="text-sm">Real-time AI feedback on your answers and body language</span>
            </div>
            <div className="flex items-start gap-3 text-white/90">
              <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
              <span className="text-sm">Detailed analytics showing your improvement areas</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
